import { z } from 'zod';

export const CATALOG_LOCALES = ['en', 'fr', 'ar', 'es'] as const;
export type CatalogLocale = (typeof CATALOG_LOCALES)[number];

/**
 * A numeric spec count (passenger/doors/baggage) that tolerates the string
 * shape the admin form and the text-backed `spec_*` DB columns send back.
 *
 * The ops entry form leaves untouched spec fields at the value it read from
 * GET /entries/:id, where the `text` columns surface `"7"` / `"5"` as strings;
 * only edited inputs run through Number(). So a save often mixes `"7"` (string)
 * and `5` (number) in the same specs object. Plain z.number() rejected the
 * strings ("Expected number, received string"); this coerces them null-safely.
 *
 * A NOTE on the null-vs-zero trap: z.coerce.number() turns both `null` and `""`
 * into 0, which would silently overwrite "unknown" with "zero passengers". So we
 * preprocess by hand instead — mirroring the intent of the api file's `year`
 * coercion — and map cleared/absent inputs to null/undefined, never 0.
 *
 * Matrix:
 *   7        -> 7            (number passes through)
 *   "7"      -> 7            (numeric string parsed)
 *   null     -> null         (preserved, NOT 0)
 *   undefined-> undefined    (field absent; still .optional())
 *   ""       -> null         (cleared input, NOT 0)
 *   "  "     -> null         (whitespace-only == cleared)
 *   "x"      -> reject        (stays a string -> z.number() fails)
 *   "1.5"    -> reject        (parsed 1.5 -> .int() fails)
 *   -1 / 100 -> reject        (out of 0..99 bounds, as before)
 */
const coerceSpecInt = z.preprocess((v) => {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return null;
    // Numeric-looking string -> number so z.number() can validate/bound it.
    // Anything non-numeric ("x") stays a string and fails z.number() below.
    const n = Number(trimmed);
    return Number.isNaN(n) ? trimmed : n;
  }
  return v;
}, z.number().int().min(0).max(99).nullable().optional());

export const CatalogSpecsSchema = z
  .object({
    passenger: coerceSpecInt,
    doors: coerceSpecInt,
    gearShift: z.string().trim().max(50).nullable().optional(),
    baggage: coerceSpecInt,
    fuelType: z.string().trim().max(50).nullable().optional(),
    bodyType: z.string().trim().max(50).nullable().optional(),
  })
  .strict();
export type CatalogSpecs = z.infer<typeof CatalogSpecsSchema>;

export const CatalogLocaleCopySchema = z
  .object({
    title: z.string().trim().max(255).optional(),
    description: z.string().trim().max(10_000).nullable().optional(),
    seoMetaTitle: z.string().trim().max(255).nullable().optional(),
    seoMetaDescription: z.string().trim().max(500).nullable().optional(),
  })
  .strict();
export type CatalogLocaleCopy = z.infer<typeof CatalogLocaleCopySchema>;

const SlugNameSchema = z
  .object({ slug: z.string().trim().min(1).max(120), name: z.string().trim().min(1).max(120) })
  .strict();

const CatalogImageSchema = z
  .object({ key: z.string().trim().min(1).max(512), hero: z.boolean().default(false) })
  .strict();

export const CatalogManifestRecordSchema = z
  .object({
    make: SlugNameSchema,
    model: SlugNameSchema,
    year: z.number().int().min(1950).max(2100),
    trim: z.string().trim().max(80).default(''),
    specs: CatalogSpecsSchema.default({}),
    features: z.array(z.string().trim().min(1).max(120)).max(200).default([]),
    locales: z
      .record(z.enum(CATALOG_LOCALES), CatalogLocaleCopySchema)
      .refine((l) => !!l.en, { message: 'en locale is required' }),
    images: z
      .array(CatalogImageSchema)
      .max(30)
      .refine((imgs) => imgs.filter((i) => i.hero).length === 1, {
        message: 'exactly one image must be hero',
      }),
  })
  .strict();
export type CatalogManifestRecord = z.infer<typeof CatalogManifestRecordSchema>;

/** Shape returned by GET /vendor/catalog/entries/:id (the auto-fill payload). */
export interface CatalogEntryPayload {
  id: number;
  slug: string;
  defaultTitle: string;
  specs: CatalogSpecs;
  locales: Partial<Record<CatalogLocale, CatalogLocaleCopy>>;
  features: number[]; // attribute_value ids
  images: { mediaId: number; url: string; hero: boolean; order: number }[];
}
